import type { GroupItem, Id, Project, Scene, SceneItem } from '../core/model';
import type { ItemLocation, ProjectQueries } from './api';

/** "Name", "Name 2", "Name 3"... first one not in `existing`. */
export function uniqueAmong(existing: Iterable<string>, base: string): string {
  const taken = new Set(existing);
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base} ${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

function locate(
  items: SceneItem[],
  itemId: Id,
  parent: GroupItem | null,
  ancestors: GroupItem[],
): ItemLocation | null {
  for (let index = 0; index < items.length; index++) {
    const item = items[index]!;
    if (item.id === itemId) return { item, parent, index, ancestors };
    if (item.type === 'group') {
      const found = locate(item.children, itemId, item, [...ancestors, item]);
      if (found) return found;
    }
  }
  return null;
}

function flatten(items: readonly SceneItem[], out: SceneItem[]): SceneItem[] {
  for (const item of items) {
    out.push(item);
    if (item.type === 'group') flatten(item.children, out);
  }
  return out;
}

function countRefs(items: readonly SceneItem[], sourceId: Id): number {
  let count = 0;
  for (const item of flatten(items, [])) if (item.type === 'source' && item.sourceId === sourceId) count++;
  return count;
}

function reachable(project: Project, sceneId: Id): Set<Id> {
  const seen = new Set<Id>();
  const visit = (id: Id): void => {
    if (seen.has(id)) return;
    seen.add(id);
    const scene = project.scenes[id];
    if (!scene) return;
    for (const item of flatten(scene.items, [])) if (item.type === 'scene') visit(item.sceneId);
  };
  visit(sceneId);
  return seen;
}

export const queries: ProjectQueries = {
  groupIssue(project, sceneId, itemIds) {
    const ids = [...new Set(itemIds)];
    if (ids.length === 0) return 'Select at least one item to group.';
    const locs = ids.map((id) => queries.findItem(project, sceneId, id));
    if (locs.some((l) => !l)) return 'Some of the selected items no longer exist.';
    const found = locs as ItemLocation[];
    const parentId = found[0]!.parent?.id ?? null;
    if (found.some((l) => (l.parent?.id ?? null) !== parentId)) {
      return 'Only items that share the same parent can be grouped together.';
    }
    if (found.some((l) => l.item.locked || l.ancestors.some((g) => g.locked))) {
      return 'Unlock the items before grouping them.';
    }
    return null;
  },

  findItem(project, sceneId, itemId) {
    const scene = project.scenes[sceneId];
    return scene ? locate(scene.items, itemId, null, []) : null;
  },

  flattenItems(scene: Scene) {
    return flatten(scene.items, []);
  },

  sourceRefCount(project, sourceId) {
    let count = 0;
    for (const scene of Object.values(project.scenes)) count += countRefs(scene.items, sourceId);
    return count;
  },

  reachableScenes: reachable,

  sourcesInScene(project, sceneId, options) {
    const visibleOnly = options?.visibleOnly ?? false;
    const sources = new Set<Id>();
    const visited = new Set<Id>();
    const walk = (items: readonly SceneItem[]): void => {
      for (const item of items) {
        if (visibleOnly && !item.visible) continue;
        if (item.type === 'source') sources.add(item.sourceId);
        else if (item.type === 'group') walk(item.children);
        else if (!visited.has(item.sceneId)) {
          visited.add(item.sceneId);
          const nested = project.scenes[item.sceneId];
          if (nested) walk(nested.items);
        }
      }
    };
    visited.add(sceneId);
    const scene = project.scenes[sceneId];
    if (scene) walk(scene.items);
    return sources;
  },

  uniqueName(project, base, among) {
    const names = Object.values(among === 'scenes' ? project.scenes : project.sources).map((x) => x.name);
    return uniqueAmong(names, base);
  },
};
